/*
 * pj-impressions.js — Puppy Jo product-card impression tracker.
 *
 * Counts a product card as SEEN when >= 50% of it stays on screen for >= 1 second,
 * once per card per page view, and sends batches to GA4 (property G-VXXT8WSKLV) as
 * `view_item_list` events whose item_list_name starts with "pj_seen_". That prefix keeps
 * these separate from the Google & YouTube app's own page-load view_item_list events.
 *
 * Feeds the bi-weekly purge scorer: >= 150 seen-impressions and 0 sales -> review for draft.
 *
 * Transport is the GA4 Measurement Protocol, not gtag.js: GA runs inside Shopify's sandboxed
 * web pixel, and loading a second gtag.js on the page would double enhanced-measurement events.
 * Events reuse the first-party _ga / _ga_<id> cookies so they join the visitor's real session.
 *
 * Debug: add ?pj_imp_debug=1 to any URL — payloads are logged and kept in
 * window.__pjImpressions instead of being sent.
 */
(function () {
  'use strict';

  var MEASUREMENT_ID = 'G-VXXT8WSKLV';
  var API_SECRET = 'lKUwmwiATDqwfTbe0tkj5w';
  var ENDPOINT = 'https://www.google-analytics.com/mp/collect';

  var VISIBLE_RATIO = 0.5;
  var DWELL_MS = 1000;
  var FLUSH_MS = 5000;
  var MAX_ITEMS_PER_EVENT = 50;

  var DEBUG = /[?&]pj_imp_debug=1\b/.test(location.search);

  if (window.__pjImpressionsLoaded) return;
  window.__pjImpressionsLoaded = true;

  if (!('IntersectionObserver' in window)) return;
  if (navigator.webdriver && !DEBUG) return;
  if (!DEBUG && API_SECRET.indexOf('__') === 0) return;

  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed();
      }
    } catch (e) {}
    return true;
  }

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  // _ga = "GA1.1.<random>.<timestamp>" -> client_id "<random>.<timestamp>"
  function clientId() {
    var v = cookie('_ga');
    if (!v) return null;
    var parts = v.split('.');
    return parts.length >= 4 ? parts[2] + '.' + parts[3] : null;
  }

  // _ga_<id> = "GS2.1.s<session_id>$o3$..." (new) or "GS1.1.<session_id>.<n>...." (old)
  function sessionId() {
    var v = cookie('_ga_' + MEASUREMENT_ID.replace(/^G-/, ''));
    if (!v) return null;
    var m = v.match(/^GS2\.\d+\.s(\d+)/) || v.match(/^GS1\.\d+\.(\d+)/);
    return m ? m[1] : null;
  }

  function listName(card) {
    var path = location.pathname;
    var section = card.closest('[id^="shopify-section-"]');
    var sectionId = section ? section.id.replace('shopify-section-', '') : 'none';
    var page;
    var m = path.match(/\/collections\/([^/]+)(?:\/products\/)?/);
    if (/\/products\//.test(path)) page = 'pdp';
    else if (m) page = 'collection:' + m[1];
    else if (/\/search/.test(path)) page = 'search';
    else if (path === '/' || path === '') page = 'home';
    else page = 'other:' + path.split('/').filter(Boolean).slice(0, 2).join('/');
    return { id: sectionId, name: 'pj_seen_' + page };
  }

  function cardTitle(card) {
    var a = card.querySelector('a[aria-label]');
    if (a && a.getAttribute('aria-label')) return a.getAttribute('aria-label').trim();
    var t = card.querySelector('[class*="title"], h3, h4');
    return t ? t.textContent.trim() : '';
  }

  var seen = {};          // "<list id>|<product id>" -> true, once per page view
  var timers = new Map(); // card element -> dwell timer
  var queue = [];         // pending items, each tagged with its list

  function record(card) {
    var pid = card.getAttribute('data-product-id');
    if (!pid) return;
    var list = listName(card);
    var key = list.id + '|' + pid;
    if (seen[key]) return;
    seen[key] = true;
    var all = Array.prototype.slice.call(
      (card.closest('[id^="shopify-section-"]') || document).querySelectorAll('product-card[data-product-id]')
    );
    queue.push({
      list: list,
      item: {
        item_id: pid,
        item_name: cardTitle(card),
        index: Math.max(0, all.indexOf(card)),
        item_list_id: list.id,
        item_list_name: list.name
      }
    });
    if (queue.length >= MAX_ITEMS_PER_EVENT) flush(false);
  }

  function buildPayload(batch) {
    var cid = clientId();
    if (!cid) return null;
    var sid = sessionId();
    var byList = {};
    batch.forEach(function (q) {
      var k = q.list.id + '|' + q.list.name;
      (byList[k] = byList[k] || { list: q.list, items: [] }).items.push(q.item);
    });
    var events = Object.keys(byList).slice(0, 25).map(function (k) {
      var g = byList[k];
      var params = {
        item_list_id: g.list.id,
        item_list_name: g.list.name,
        engagement_time_msec: 1,
        page_location: location.href.split('#')[0],
        items: g.items
      };
      if (sid) params.session_id = sid;
      return { name: 'view_item_list', params: params };
    });
    return { client_id: cid, events: events };
  }

  function flush(unloading) {
    if (!queue.length || !analyticsAllowed()) return;
    var batch = queue.splice(0, queue.length);
    var payload = buildPayload(batch);
    if (!payload) return;
    var body = JSON.stringify(payload);
    if (DEBUG) {
      (window.__pjImpressions = window.__pjImpressions || []).push(payload);
      console.log('[pj-impressions]', batch.length, 'items', payload);
      return;
    }
    var url = ENDPOINT + '?measurement_id=' + MEASUREMENT_ID + '&api_secret=' + API_SECRET;
    if (unloading && navigator.sendBeacon) {
      navigator.sendBeacon(url, body);
    } else {
      fetch(url, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' }).catch(function () {});
    }
  }

  var dbg = DEBUG ? (window.__pjDebug = []) : null;
  function note(m) { if (dbg) dbg.push(Math.round(performance.now()) + ' ' + m); }

  // Note: IntersectionObserver only reports when the page paints frames, so a background tab
  // records nothing — which is correct (not seen). It also means a hidden automation pane
  // records nothing; test with the pane visible.
  var io = null;

  function onEntries(entries) {
    entries.forEach(function (e) {
      var card = e.target;
      note('io ' + card.getAttribute('data-product-id') + ' ' + e.isIntersecting + ' ' + e.intersectionRatio.toFixed(2));
      if (e.isIntersecting && e.intersectionRatio >= VISIBLE_RATIO) {
        if (!timers.has(card)) {
          timers.set(card, setTimeout(function () {
            timers.delete(card);
            record(card);
            io.unobserve(card);
          }, DWELL_MS));
        }
      } else if (timers.has(card)) {
        clearTimeout(timers.get(card));
        timers.delete(card);
      }
    });
  }

  var watched = new WeakSet();
  function scan(root) {
    var cards = (root || document).querySelectorAll('product-card[data-product-id]');
    note('scan ' + cards.length);
    for (var i = 0; i < cards.length; i++) {
      if (!watched.has(cards[i])) {
        watched.add(cards[i]);
        io.observe(cards[i]);
      }
    }
  }

  io = new IntersectionObserver(onEntries, { threshold: [0, VISIBLE_RATIO, 1] });
  scan();
  // Cards added later (infinite scroll, filters, lazily rendered recommendation rows).
  // This script never writes to the DOM, so observing body cannot feed back on itself.
  // Debounced: carts, popups and infinite scroll add nodes in bursts; rescan once per burst.
  var scanTimer = null;
  new MutationObserver(function (muts) {
    if (scanTimer) return;
    for (var i = 0; i < muts.length; i++) {
      if (muts[i].addedNodes.length) {
        scanTimer = setTimeout(function () { scanTimer = null; scan(); }, 250);
        return;
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  setInterval(function () { flush(false); }, FLUSH_MS);
  addEventListener('pagehide', function () { flush(true); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush(true);
  });
})();
